import { useMemo, useRef, useState } from "react";
import { isShoppingList } from "../../../shared/kitchen/board";
import type { ItemSuggestion, ListItem } from "../../../shared/kitchen/models";
import { itemKey } from "../../../shared/kitchen/validate";
import { useAppUi } from "../../app/uiContext";
import { dataStateOf } from "../../data/queries";
import { Icon } from "../../ui/Icon";
import { Confirm, Sheet } from "../../ui/Sheet";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { isTempId, tempId, useListActions, useLists } from "./api";
import { ShoppingBoardScreen } from "./ShoppingBoard";
import "./kitchen.css";

interface ListsConfig {
  /** List name to show; blank = every list, as tabs. */
  list: string;
  showSuggestions: boolean;
}

/** Rank suggestions: frequently and recently used first. */
function rankSuggestions(s: ItemSuggestion[], now: number): ItemSuggestion[] {
  const score = (x: ItemSuggestion) => Math.log2(1 + x.uses) * 2 + Math.max(0, 30 - (now - Date.parse(x.lastUsedAt)) / 86_400_000) / 10;
  return [...s].sort((a, b) => score(b) - score(a));
}

function Lists({ config, size, editing, fullscreen, openFullscreen }: WidgetProps<ListsConfig>) {
  const q = useLists();
  const actions = useListActions();
  const { toast } = useAppUi();
  const state = dataStateOf(q, 10 * 60_000);
  const [tab, setTab] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [qty, setQty] = useState("");
  const [showQty, setShowQty] = useState(false);
  const [editItem, setEditItem] = useState<ListItem | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const lists = q.data?.lists ?? [];
  const pinned = config.list.trim() ? lists.find((l) => l.name.toLowerCase() === config.list.trim().toLowerCase()) : undefined;
  const current = pinned ?? lists.find((l) => l.id === tab) ?? lists[0];
  const showTabs = !pinned && lists.length > 1;

  const items = useMemo(() => (q.data?.items ?? []).filter((i) => i.listId === current?.id), [q.data, current?.id]);
  const open = items.filter((i) => !i.checkedAt);
  const checked = items.filter((i) => i.checkedAt).sort((a, b) => (b.checkedAt ?? "").localeCompare(a.checkedAt ?? ""));
  const typed = itemKey(text);
  const listId = current?.id;
  const limit = size.variant === "compact" ? 6 : 12;
  const suggestions = useMemo(() => {
    if (!listId || !config.showSuggestions || !q.data) return [];
    const openKeys = new Set(q.data.items.filter((i) => i.listId === listId && !i.checkedAt).map((i) => itemKey(i.text)));
    const mine = q.data.suggestions.filter((s) => s.listId === listId && !openKeys.has(itemKey(s.text)));
    const ranked = rankSuggestions(mine, Date.now());
    // While typing, suggestions become autocomplete.
    return (typed ? ranked.filter((s) => itemKey(s.text).includes(typed) && itemKey(s.text) !== typed) : ranked).slice(0, limit);
  }, [q.data, listId, typed, config.showSuggestions, limit]);
  const countOpenFor = (listId: string) => (q.data?.items ?? []).filter((i) => i.listId === listId && !i.checkedAt).length;

  const add = (value: string, quantity: string | null = null) => {
    const clean = value.replace(/\s+/g, " ").trim();
    if (!clean || !current) return;
    actions.add.mutate(
      { listId: current.id, text: clean.slice(0, 120), quantity: quantity?.trim() ? quantity.trim().slice(0, 30) : null, tempId: tempId("item") },
      { onError: (e) => toast(`Couldn’t add “${clean}”: ${e.message}`) },
    );
  };
  const submit = () => {
    add(text, qty);
    setText("");
    setQty("");
    input.current?.focus();
  };
  const toggle = (i: ListItem) => actions.toggle.mutate(i, { onError: (e) => toast(`Couldn’t update “${i.text}”: ${e.message}`) });
  const remove = (i: ListItem) =>
    actions.remove.mutate(i, {
      onSuccess: () => toast(`Removed “${i.text}”`, { label: "Undo", run: () => add(i.text, i.quantity) }),
      onError: (e) => toast(`Couldn’t remove “${i.text}”: ${e.message}`),
    });
  const clearChecked = () => {
    if (!current) return;
    setConfirmClear(false);
    actions.clearChecked.mutate(current.id, { onError: (e) => toast(`Couldn’t clear checked items: ${e.message}`) });
  };

  const compact = size.variant === "compact" && !fullscreen;

  return (
    <div className={`kl ${compact ? "is-compact" : ""}`}>
      <WidgetHeader
        title={pinned?.name ?? (showTabs ? "Lists" : (current?.name ?? "Lists"))}
        meta={open.length ? `${open.length} to get` : undefined}
        onTitle={fullscreen ? undefined : openFullscreen}
        actions={
          checked.length > 0 || (current && isShoppingList(current)) ? (
            <>
              {checked.length > 0 && (
                <button className="btn btn-sm kl-clear" onClick={() => setConfirmClear(true)} disabled={editing} aria-label={`Clear ${checked.length} checked`}>
                  {compact ? "Clear" : `Clear ${checked.length} checked`}
                </button>
              )}
              {current && isShoppingList(current) && (
                <button className="btn btn-sm kl-board" onClick={() => setBoardOpen(true)} disabled={editing} aria-label="Write the list on the shopping board" title="Shopping board">
                  <Icon name="pen" size="1.05rem" />
                  {!compact && "Write"}
                </button>
              )}
            </>
          ) : undefined
        }
      />
      {showTabs && (
        <div className="chip-row kl-tabs" role="tablist" aria-label="Lists">
          {lists.map((l) => {
            const n = countOpenFor(l.id);
            return (
              <button key={l.id} role="tab" aria-selected={current?.id === l.id} className={`chip chip-sm ${current?.id === l.id ? "on" : ""}`} onClick={() => setTab(l.id)} disabled={editing}>
                {l.name}
                {n > 0 && <span className="kl-count">{n}</span>}
              </button>
            );
          })}
        </div>
      )}

      {!q.data ? (
        state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="list" title="Lists unavailable" detail={q.error?.message} />
      ) : !current ? (
        <EmptyState icon="list" title={config.list.trim() ? `No list called “${config.list.trim()}”` : "No lists yet"} detail="An adult can add lists in Settings → Kitchen." />
      ) : (
        <>
          <form
            className="kl-add"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <input
              ref={input}
              className="input input-lg kl-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Add to ${current.name}…`}
              maxLength={120}
              enterKeyHint="done"
              autoComplete="off"
              aria-label={`Add to ${current.name}`}
              disabled={editing}
            />
            {showQty && !compact && (
              <input className="input input-lg kl-qty" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qty" maxLength={30} aria-label="Quantity (optional)" disabled={editing} />
            )}
            {!compact && (
              <button type="button" className={`icon-btn kl-qty-toggle ${showQty ? "on" : ""}`} onClick={() => setShowQty((s) => !s)} aria-pressed={showQty} aria-label="Add a quantity" disabled={editing}>
                <span aria-hidden="true">#</span>
              </button>
            )}
            <button type="submit" className="icon-btn icon-btn-accent kl-add-btn" aria-label="Add item" disabled={editing || !text.trim()}>
              <Icon name="plus" />
            </button>
          </form>
          {suggestions.length > 0 && (
            <div className={`chip-row kl-suggest ${compact ? "" : "wrap"}`} aria-label={typed ? "Matching items" : "Add again"}>
              {suggestions.map((s) => (
                <button
                  key={s.text}
                  className="chip chip-sm"
                  onClick={() => {
                    add(s.text);
                    if (typed) setText("");
                  }}
                  disabled={editing}
                >
                  <Icon name="plus" size="0.85rem" />
                  {s.text}
                </button>
              ))}
            </div>
          )}
          {open.length === 0 && checked.length === 0 ? (
            <EmptyState icon="check" title="Nothing on the list" detail={suggestions.length ? "Tap a suggestion or type to add." : "Type above to add something."} />
          ) : (
            <ul className="kl-items">
              {open.map((i) => (
                <ItemRow key={i.id} item={i} editing={editing} onToggle={toggle} onEdit={setEditItem} onRemove={remove} />
              ))}
              {checked.length > 0 && open.length > 0 && <li className="kl-divider" aria-hidden="true" />}
              {checked.map((i) => (
                <ItemRow key={i.id} item={i} editing={editing} onToggle={toggle} onEdit={setEditItem} onRemove={remove} />
              ))}
            </ul>
          )}
        </>
      )}
      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
      {boardOpen && current && <ShoppingBoardScreen list={current} onClose={() => setBoardOpen(false)} />}
      {editItem && <ItemEditor item={editItem} onClose={() => setEditItem(null)} onRemove={remove} />}
      {confirmClear && current && (
        <Confirm
          title="Clear checked items?"
          message={`${checked.length} checked item${checked.length === 1 ? "" : "s"} will be removed from ${current.name}. They stay in the suggestions.`}
          confirmLabel="Clear"
          onCancel={() => setConfirmClear(false)}
          onConfirm={clearChecked}
        />
      )}
    </div>
  );
}

function ItemRow({ item, editing, onToggle, onEdit, onRemove }: { item: ListItem; editing: boolean; onToggle: (i: ListItem) => void; onEdit: (i: ListItem) => void; onRemove: (i: ListItem) => void }) {
  const done = !!item.checkedAt;
  const pending = isTempId(item.id);
  return (
    <li className={`kl-item ${done ? "done" : ""} ${pending ? "pending" : ""}`}>
      <button className={`task-check ${done ? "checked" : ""}`} onClick={() => onToggle(item)} aria-label={done ? `Uncheck ${item.text}` : `Check off ${item.text}`} aria-pressed={done} disabled={editing || pending}>
        <span className="box">{done && <Icon name="check" size="1rem" />}</span>
      </button>
      <button className="kl-body" onClick={() => onEdit(item)} disabled={editing || pending}>
        <span className="kl-text">{item.text}</span>
        {item.quantity && <span className="kl-qty-pill">{item.quantity}</span>}
      </button>
      <button className="icon-btn kl-del" onClick={() => onRemove(item)} aria-label={`Remove ${item.text}`} disabled={editing || pending}>
        <Icon name="close" size="1.05rem" />
      </button>
    </li>
  );
}

function ItemEditor({ item, onClose, onRemove }: { item: ListItem; onClose: () => void; onRemove: (i: ListItem) => void }) {
  const actions = useListActions();
  const { toast } = useAppUi();
  const [text, setText] = useState(item.text);
  const [qty, setQty] = useState(item.quantity ?? "");
  const save = () => {
    const t = text.replace(/\s+/g, " ").trim();
    if (!t) return;
    actions.update.mutate({ item, text: t, quantity: qty.trim() || null }, { onError: (e) => toast(`Couldn’t save “${t}”: ${e.message}`) });
    onClose();
  };
  return (
    <Sheet
      title="Edit item"
      size="sm"
      onClose={onClose}
      footer={
        <>
          <button
            className="btn btn-danger-ghost"
            onClick={() => {
              onRemove(item);
              onClose();
            }}
          >
            <Icon name="trash" size="1.1rem" /> Remove
          </button>
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={!text.trim()}>
            Save
          </button>
        </>
      }
    >
      <div className="form">
        <label className="field">
          <span>Item</span>
          <input className="input input-lg" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} maxLength={120} />
        </label>
        <label className="field">
          <span>Quantity</span>
          <input className="input" value={qty} onChange={(e) => setQty(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} maxLength={30} placeholder="e.g. 2, 1 lb, a big bag" />
        </label>
        <div className="chip-row wrap">
          {["1", "2", "3", "6", "12", "1 lb", "2 lb"].map((v) => (
            <button key={v} className={`chip chip-sm ${qty === v ? "on" : ""}`} onClick={() => setQty(qty === v ? "" : v)}>
              {v}
            </button>
          ))}
        </div>
      </div>
    </Sheet>
  );
}

export const listsWidget: WidgetDefinition<ListsConfig> = {
  type: "lists",
  version: 1,
  name: "Shopping & lists",
  description: "The shopping list and other household lists. Check things off, re-add favourites in one tap.",
  icon: "list",
  category: "household",
  defaultSize: { w: 4, h: 6 },
  minSize: { w: 3, h: 3 },
  stackHeight: 480,
  defaults: { list: "", showSuggestions: true },
  settings: [
    { key: "list", label: "List", type: "text", maxLength: 40, placeholder: "All lists (tabs)", help: "Type a list’s name (e.g. Shopping list) to show only that list, or leave blank for tabs of every list." },
    { key: "showSuggestions", label: "Suggest items to add again", type: "toggle" },
  ],
  provider: "kinmantle-kitchen",
  supportsFullscreen: true,
  Component: Lists,
};
